import { profileApi } from '../profileApi';
import type {
  PortalUserView,
  InvitePortalUserRequest,
  InvitePortalUserResult,
  UpdatePortalUserRequest,
  DisablePortalUserResult,
  UnassignPortalUserResult,
  ResendInvitationResult,
} from '../types/portalUserTypes';

const portalUserApi = profileApi.injectEndpoints({
  endpoints: (builder) => ({
    // GET /portal-users?practiceId=xxx
    getPortalUsers: builder.query<PortalUserView[], { practiceId: string }>({
      query: ({ practiceId }) => `portal-users?practiceId=${encodeURIComponent(practiceId)}`,
      providesTags: (_result, _error, { practiceId }) => [{ type: 'PortalUsers', id: practiceId }],
    }),

    // POST /portal-users/invite
    invitePortalUser: builder.mutation<InvitePortalUserResult, InvitePortalUserRequest>({
      query: (body) => ({
        url: 'portal-users/invite',
        method: 'POST',
        body,
      }),
      async onQueryStarted({ practiceId, email, firstName, lastName }, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;
          if (data.success && data.oid) {
            dispatch(
              portalUserApi.util.updateQueryData('getPortalUsers', { practiceId }, (draft) => {
                draft.push({
                  oid: data.oid!,
                  displayName: `${firstName} ${lastName}`,
                  mail: email,
                  status: 'Pending',
                  lastSignInDateTime: null,
                  invitedAt: new Date().toISOString(),
                  expiresAt: data.expiresAt ?? null,
                  practiceId,
                  practiceName: '',
                });
              })
            );
          }
        } catch {
          // mutation failed — leave cache as-is
        }
      },
    }),

    // PATCH /portal-users/:oid
    updatePortalUser: builder.mutation<void, { oid: string; body: UpdatePortalUserRequest; practiceId: string }>({
      query: ({ oid, body }) => ({
        url: `portal-users/${oid}`,
        method: 'PATCH',
        body,
      }),
      invalidatesTags: (_result, _error, { practiceId }) => [{ type: 'PortalUsers', id: practiceId }],
    }),

    // POST /portal-users/:oid/disable
    disablePortalUser: builder.mutation<DisablePortalUserResult, { oid: string; practiceId: string }>({
      query: ({ oid }) => ({
        url: `portal-users/${oid}/disable`,
        method: 'POST',
      }),
      async onQueryStarted({ oid, practiceId }, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          // Optimistically remove the user from the cache since Entra
          // deletion may not propagate before the next refetch.
          dispatch(
            portalUserApi.util.updateQueryData('getPortalUsers', { practiceId }, (draft) => {
              const idx = draft.findIndex((u) => u.oid === oid);
              if (idx !== -1) draft.splice(idx, 1);
            })
          );
        } catch {
          // mutation failed — leave cache as-is
        }
      },
    }),

    // POST /portal-users/:oid/unassign
    unassignPortalUser: builder.mutation<UnassignPortalUserResult, { oid: string; practiceId: string }>({
      query: ({ oid }) => ({
        url: `portal-users/${oid}/unassign`,
        method: 'POST',
      }),
      invalidatesTags: (_result, _error, { practiceId }) => [{ type: 'PortalUsers', id: practiceId }],
    }),

    // POST /portal-users/:oid/resend-invite
    resendPortalUserInvite: builder.mutation<ResendInvitationResult, { oid: string; practiceId: string }>({
      query: ({ oid }) => ({
        url: `portal-users/${oid}/resend-invite`,
        method: 'POST',
      }),
      invalidatesTags: (_result, _error, { practiceId }) => [{ type: 'PortalUsers', id: practiceId }],
    }),
  }),
  overrideExisting: false,
});

export const {
  useGetPortalUsersQuery,
  useInvitePortalUserMutation,
  useUpdatePortalUserMutation,
  useDisablePortalUserMutation,
  useUnassignPortalUserMutation,
  useResendPortalUserInviteMutation,
} = portalUserApi;
